import React, { useState, useEffect } from 'react';
import Navbar from '../components/navbar/navbar';
import Footer from '../components/footer';
import api from '../utils/api';

const defaultSections = [
    { id: 'info', icon: 'fa-database', title: 'Information We Collect', content: [
        'We collect information you provide directly, such as your name, email address, phone number, and property preferences when you create an account or submit an inquiry.',
        'We automatically collect certain information when you visit our platform, including your IP address, browser type, device information, and browsing activity on our site.',
        'When you schedule a tour or contact a builder, we collect the details you share including preferred dates, times, and property interests.',
    ]},
    { id: 'use', icon: 'fa-gear', title: 'How We Use Your Information', content: [
        'To provide and improve our real estate platform services, including personalized property recommendations.',
        'To connect you with builders and developers when you express interest in their projects.',
        'To send you relevant notifications about new projects, price updates, and offers based on your preferences.',
        'To process your tour scheduling requests and facilitate communication between buyers and builders.',
        'To analyze usage patterns and improve our website performance and user experience.',
    ]},
    { id: 'share', icon: 'fa-share-nodes', title: 'Information Sharing', content: [
        'We share your contact information with builders and developers only when you explicitly request to be contacted or schedule a tour.',
        'We do not sell, rent, or trade your personal information to third parties for marketing purposes.',
        'We may share anonymized, aggregated data with partners for analytical purposes.',
        'We may disclose information when required by law or to protect the rights and safety of our users and platform.',
    ]},
    { id: 'security', icon: 'fa-shield-halved', title: 'Data Security', content: [
        'We implement industry-standard security measures including encryption, secure servers, and access controls to protect your data.',
        'Your account is protected by a password, and we recommend using a strong, unique password for your My Property Guru account.',
        'While we strive to protect your information, no method of transmission over the Internet is 100% secure.',
    ]},
    { id: 'cookies', icon: 'fa-cookie-bite', title: 'Cookies & Tracking', content: [
        'We use cookies and similar technologies to enhance your browsing experience and remember your preferences.',
        'These cookies help us analyze site traffic, show relevant property suggestions, and improve our services.',
        'You can control cookie settings through your browser preferences. Disabling cookies may affect some features.',
    ]},
    { id: 'rights', icon: 'fa-user-shield', title: 'Your Rights', content: [
        'You have the right to access, update, or delete your personal information at any time through your account settings.',
        'You can opt out of marketing communications by clicking the unsubscribe link in our emails or updating your notification preferences.',
        'You may request a copy of all personal data we hold about you by contacting us at info@mypropertyguru.in.',
    ]},
    { id: 'contact', icon: 'fa-envelope', title: 'Contact Us', content: [
        'For any privacy-related questions or concerns, please contact us:',
        'Email: info@mypropertyguru.in | Phone: +91 95370 02666',
        'Address: Ahmedabad, Gujarat, India',
    ]},
];

export default function PrivacyPolicy() {
    const [content, setContent] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const data = await api.getSystemSettings();
                if (data && data.privacyPolicy) {
                    setContent(data.privacyPolicy);
                }
            } catch (err) {
                console.error('Failed to fetch privacy policy:', err);
            } finally {
                setLoading(false);
            }
        };
        fetchSettings();
    }, []);

    return (
        <>
            <Navbar transparent={false} />
            <section style={{
                background: 'linear-gradient(135deg, #05144b 0%, #0a2a8d 50%, #1e40af 100%)',
                padding: '80px 0 60px', position: 'relative', overflow: 'hidden'
            }}>
                <div style={{ position: 'absolute', bottom: '-40px', left: '10%', width: '200px', height: '200px', borderRadius: '50%', background: 'rgba(241,174,36,0.08)' }}></div>
                <div className="container position-relative" style={{ zIndex: 2 }}>
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <div style={{ width: '40px', height: '3px', background: '#f1ae24', borderRadius: '2px' }}></div>
                        <span style={{ color: '#f1ae24', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px' }}>Legal</span>
                    </div>
                    <h1 className="text-white fw-bold mb-3" style={{ fontSize: '2.5rem' }}>Privacy <span style={{ color: '#f1ae24' }}>Policy</span></h1>
                    <p className="mb-0" style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '600px', lineHeight: 1.7 }}>
                        Your privacy matters to us. Learn how we collect, use, and protect your personal information.
                    </p>
                </div>
            </section>

            <section style={{ background: '#f8f9fa', padding: '60px 0 80px', minHeight: '400px' }}>
                <div className="container">
                    {loading ? (
                        <div className="text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    ) : content ? (
                        <div className="bg-white rounded-4 shadow-sm p-5" style={{ border: '1px solid #eee' }}>
                            <div className="legal-content-dynamic" dangerouslySetInnerHTML={{ __html: content }} />
                            <style>{`
                                .legal-content-dynamic h1, .legal-content-dynamic h2, .legal-content-dynamic h3 {
                                    font-weight: 700;
                                    color: #05144b;
                                    margin-top: 1.5rem;
                                    margin-bottom: 1rem;
                                }
                                .legal-content-dynamic p {
                                    color: #555;
                                    line-height: 1.8;
                                    margin-bottom: 1rem;
                                }
                                .legal-content-dynamic ul, .legal-content-dynamic ol {
                                    margin-bottom: 1.5rem;
                                }
                                .legal-content-dynamic li {
                                    color: #555;
                                    line-height: 1.8;
                                    margin-bottom: 0.5rem;
                                }
                            `}</style>
                        </div>
                    ) : (
                        <div className="row g-4">
                            {/* Quick Nav */}
                            <div className="col-lg-3">
                                <div className="bg-white rounded-4 shadow-sm p-3 sticky-top" style={{ top: '100px', zIndex: 1 }}>
                                    <h6 className="fw-bold text-muted mb-3 px-2" style={{ fontSize: '0.7rem', textTransform: 'uppercase', letterSpacing: '1px' }}>On This Page</h6>
                                    {defaultSections.map((s, i) => (
                                        <a key={i} href={`#${s.id}`} className="d-flex align-items-center gap-2 rounded-3 mb-1 px-3 py-2 text-decoration-none"
                                            style={{ color: '#333', fontWeight: 500, fontSize: '0.85rem', transition: 'all 0.2s' }}>
                                            <i className={`fa-solid ${s.icon}`} style={{ width: '16px', color: '#999', fontSize: '12px' }}></i>
                                            {s.title}
                                        </a>
                                    ))}
                                </div>
                            </div>

                            {/* Content */}
                            <div className="col-lg-9">
                                {defaultSections.map((s, i) => (
                                    <div key={i} id={s.id} className="bg-white rounded-4 shadow-sm p-4 mb-4" style={{ border: '1px solid #eee' }}>
                                        <div className="d-flex align-items-center gap-3 mb-3">
                                            <div style={{ width: '42px', height: '42px', borderRadius: '10px', background: '#05144b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                <i className={`fa-solid ${s.icon} text-white`} style={{ fontSize: '14px' }}></i>
                                            </div>
                                            <h5 className="fw-bold mb-0">{s.title}</h5>
                                        </div>
                                        <ul style={{ paddingLeft: '20px', margin: 0 }}>
                                            {s.content.map((item, j) => (
                                                <li key={j} style={{ color: '#555', lineHeight: 1.8, fontSize: '0.9rem', marginBottom: '10px', listStyleType: 'disc' }}>{item}</li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </section>
            <Footer />
        </>
    );
}
